import React, { useState, useRef, useEffect } from 'react';
import { SearchBox } from '@mapbox/search-js-react';
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import axios from 'axios';
import Spinner from '../../../Spinner';
import { useUserContext } from '../../../../contexts/UserContext.js';
import moment from 'moment-timezone';

export default function EnterAddress(props) {
    const {
        is3D,
        setIs3D
    } = useUserContext();

    const {
        map,
        getToken,
        setTeamAreas,
        teamAreas,
        myAreas,
        setMyAreas,
        plotAreas,
        setPanel,
        teams
    } = props;

    const locationRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [addToTeam, setAddToTeam] = useState(false);
    const [selectedTeams, setSelectedTeams] = useState([])

    const [circle, setCircle] = useState({
        coordinates: [],
        name: '',
        description: '',
    })

    const [radius, setRadius] = useState(5);

    // Update description when radius changes
    useEffect(() => {
        if (circle.coordinates.length > 0) {
            setCircle(prev => ({
                ...prev,
                description: `Proximity: ${radius} miles (${(radius * 1.60934).toFixed(2)} Kilometers)`
            }));
        }
    }, [radius, circle.coordinates.length]);

    const formatPolicyAndUniqueIDs = (input) => {
        if (input.includes("Policy ID") && input.includes("Unique ID")) {
          return input.split(',')
                      .map(part => part.trim())
                      .join('<br />');  // Use <br /> tags for HTML line breaks
        }
        return input;
      };
    
      const convertTo12HourFormat = (dateTimeString) => {
        const time = moment.tz(dateTimeString, "UTC");
      
        const localTime = time.tz(moment.tz.guess()).format('YYYY-MM-DD h:mm A');
      
        return localTime;
    }

    const getCentroid = (rings) => {
        let first = rings[0], last = rings[rings.length-1];

        if (first[0] != last[0] || first[1] != last[1]) 
            rings.push(first);

        let twicearea = 0, x=0, y=0, nrings = rings.length, p1, p2, f;

        for ( let i = 0, j = nrings-1 ; i < nrings ; j=i++ ) {
           p1 = rings[i]; p2 = rings[j];
           f = p1[0]*p2[1] - p2[0]*p1[1];
           twicearea += f;          
           x += ( p1[0] + p2[0] ) * f;
           y += ( p1[1] + p2[1] ) * f;
        }
        f = twicearea * 3;
        return [ x/f, y/f ];
    }

    const generateCircleCoordinates = (center, radius, points = 64) => {
        const [centerLng, centerLat] = center;
        const coordinates = [];
      
        for (let i = 0; i < points; i++) {
            const angle = (i * 360) / points;
            const angleRad = (angle * Math.PI) / 180;

            const latitude = centerLat + (radius / 69) * Math.cos(angleRad);
            const longitude =
                centerLng +
                (radius / (69 * Math.cos((centerLat * Math.PI) / 180))) *
                    Math.sin(angleRad);

            coordinates.push([longitude, latitude]);
        }
        coordinates.push(coordinates[0]);
        return coordinates;
    };

    useEffect(() => {
        if (!circle.coordinates.length || !map.current) return;
    
        const circleCoordinates = generateCircleCoordinates(circle.coordinates, radius);
    
        const sourceId = 'Proximity Area Source - polygon';
        const layerId = 'Proximity Area Layer - polygon';
    
        const geometry = {
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [circleCoordinates],
            },
        };
    
        if (map.current.getSource(sourceId)) {
            const source = map.current.getSource(sourceId);
            source.setData(geometry);
        } else {

            map.current.addSource(sourceId, {
                type: 'geojson',
                data: geometry,
            });
    
            map.current.addLayer({
                id: layerId,
                type: 'fill',
                source: sourceId,
                layout: {},
                paint: {
                    'fill-color': 'rgba(240, 240, 240, 0.4)',
                },
            });
        }

        map.current.fitBounds(turf.bbox(geometry), {
            padding: 200,
            maxZoom: 14,
            pitch: 0
        });

        if (is3D) {
            setIs3D(false);
        }
    }, [circle.coordinates, radius, map]);
    
    useEffect(() => {
        return () => {
            if (locationRef.current) {
                locationRef.current.remove();
            }
            if (map.current) {
                const sourceId = 'Proximity Area Source - polygon';
                const layerId = 'Proximity Area Layer - polygon';

                if (map.current.getLayer(layerId)) {
                    map.current.removeLayer(layerId);
                }
                if (map.current.getSource(sourceId)) {
                    map.current.removeSource(sourceId);
                }
            }
        };
    }, [map]);

    const handleTeamCheck = (team, isChecked) => {
        if (isChecked) {
          setSelectedTeams(prevTeams => [...prevTeams, team]);
        } else {
          setSelectedTeams(prevTeams =>
            prevTeams.filter(item => item.id !== team.id)
          );
        }
    };

    const WKTtoRings = (wkt) => {
        const rings = wkt.slice(9).slice(0, -2).split(',').map((coordinates) => {
            return coordinates.split(' ').filter((element) => element !== '').map((element) => parseFloat(element));
        });
        return rings;
    };

    const convertMetersToSquareMiles = (wkt) => {
        const data = {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [WKTtoRings(wkt)]
                }
              }
            ]
        };
    
        const areaInMetersSquared = turf.area(data);
          
        const conversionFactor = 0.000000386102;
        const areaInSquareMiles = areaInMetersSquared * conversionFactor;
        return Math.round(areaInSquareMiles * 100) / 100;
      };

    const returnAreaName = (data) => {
        if (data.full_address) {
           return data.full_address
        } else if (data.address) {
            return data.address
        } else if (data.name) {
           return data.name
        } else {
            return 'Unknown';
        }
    };
    
    const savePolygon = () => {
        setLoading(true);
        setError(false);

        if (!addToTeam) {
            getToken().then((token) => {
                axios.post(`https://firewatch-api.space-eyes.com/locations`, {
                    team_id: null,
                    id: null,
                    name: circle.name,
                    description: circle.description,
                    type: 'point',
                    value: null,
                    radius_miles: radius,
                    geometry: null,
                    location: circle.coordinates,
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }).then((res) => {
                    setLoading(false);
   
                    setMyAreas([res.data, ...myAreas]);

                    plotAreas(map, [res.data, ...myAreas, ...(teamAreas || [])]);
                        
                    const layers = map.current.getStyle().layers.filter((layer) => layer.id.includes('Layer-areas-focused'));

                    if (layers.length > 0) {
                        layers.forEach((layer) => map.current.removeLayer(layer.id));
                    }
            
                    if (map.current.getStyle().sources[`Source-areas-focused-${res.data.id}`]) {
                        map.current.removeSource(`Source-areas-focused-${res.data.id}`);
                    }

                    const location = [res.data.longitude, res.data.latitude];

                    const existingPopups = document.getElementsByClassName('popup');
                    Array.from(existingPopups).forEach((popup) => popup.remove());
            
                    new mapboxgl.Popup({ offset: 25, maxWidth: '600px', className: 'popup' })
                    .setLngLat(location)
                    .setHTML(`
                        <div>
                        <div class='alert-popup-title'>${res.data.name}</div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Description:</div>
                            <div class='alert-popup-row-value'>${res.data.description ? formatPolicyAndUniqueIDs(res.data.description) : 'No Description'}</div>
                        </div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Type:</div>
                            <div class='alert-popup-row-value'>${res.data.type === 'point' ? 'POINT/ADDRESS' : 'POLYGON'}</div>
                        </div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Radius:</div>
                            <div class='alert-popup-row-value'>
                                ${res.data.radius_miles} miles
                            </div>
                        </div>
                        ${res.data.value ?
                            `<div class='alert-popup-row'>
                                <div class='alert-popup-row-name'>Total Asset Value:</div>
                                <div class='alert-popup-row-value'>
                                $${Number(res.data.value).toLocaleString()}
                                </div>
                            </div>` : ' '}
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Created</div>
                            <div class='alert-popup-row-value'>${convertTo12HourFormat(res.data.created_timestamp)}</div>
                        </div>
                        </div>
                    `)
                    .addTo(map.current);
  
                    const generateCircleCoordinates = (center, radius, points = 64) => {
                        const [centerLng, centerLat] = center;
                        const coordinates = [];
                      
                        for (let i = 0; i < points; i++) {
                            const angle = (i * 360) / points;
                            const angleRad = (angle * Math.PI) / 180;
                      
                            const latitude = centerLat + (radius / 69) * Math.cos(angleRad);
                            const longitude =
                                centerLng +
                                (radius / (69 * Math.cos((centerLat * Math.PI) / 180))) *
                                    Math.sin(angleRad);
                      
                            coordinates.push([longitude, latitude]);
                        }
                        coordinates.push(coordinates[0]);
                        return coordinates;
                      };
                  
                    const circleCoordinates = generateCircleCoordinates([res.data.longitude, res.data.latitude], res.data.radius_miles);
        
                    const geometry = {
                        type: 'Feature',
                        properties: res.data,
                        geometry: {
                            type: 'Polygon',
                            coordinates: [circleCoordinates],
                        }
                    }
                    
                    map.current.addSource(`Source-areas-focused-${res.data.id}`, {
                        type: 'geojson',
                        data: {
                            type: 'FeatureCollection',
                            features: [geometry],
                        },
                    });
            
                    map.current.addLayer({
                        'id': `Layer-areas-focused-${res.data.id}-fill`,
                        'type': 'fill',
                        'source': `Source-areas-focused-${res.data.id}`,
                        'layout': {},
                        'paint': {
                          'fill-color': 'rgba(240, 240, 240, 0.4)',
                        }
                    });
          
                    map.current.addLayer({
                        'id': `Layer-areas-focused-${res.data.id}-outline`,
                        'type': 'line',
                        'source': `Source-areas-focused-${res.data.id}`,
                        'layout': {},
                        'paint': {
                          'line-color': 'rgba(255, 255, 255, 1)',
                          'line-width': 2
                        }
                    });
        
                    map.current.fitBounds(turf.bbox(geometry), {
                        padding: 100,
                        pitch: 0
                    });

                    setPanel({
                        name: 'Empty', 
                        content: null
                    });
                }).catch((err) => {
                    console.error('Error adding areas:', err);
                    setLoading(false);
                    setError('Error creating Monitoring Area. Please try again.');
                }
                );
            }).catch((error) => console.log('error getting token', error));
        } else if (selectedTeams.length === 0) {
            setError('Please choose at least one team.');
            setLoading(false);
            return;
        } else if (selectedTeams.length > 0) {
            selectedTeams.forEach((team) => {
                getToken().then((token) => {
                    axios.post(`https://firewatch-api.space-eyes.com/locations`, {
                        team_id: team.id,
                        id: null,
                        name: circle.name,
                        description: circle.description,
                        type: 'point',
                        value: null,
                        radius_miles: radius,
                        geometry: null,
                        location: circle.coordinates,
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }).then((res) => {
                    setLoading(false);
   
                    setTeamAreas([res.data, ...teamAreas]);

                    plotAreas(map, [res.data, ...(myAreas || []), ...teamAreas]);
                        
                    const layers = map.current.getStyle().layers.filter((layer) => layer.id.includes('Layer-areas-focused'));

                    if (layers.length > 0) {
                        layers.forEach((layer) => map.current.removeLayer(layer.id));
                    }
            
                    if (map.current.getStyle().sources[`Source-areas-focused-${res.data.id}`]) {
                        map.current.removeSource(`Source-areas-focused-${res.data.id}`);
                    }

                    const location = [res.data.longitude, res.data.latitude];

                    const existingPopups = document.getElementsByClassName('popup');
                    Array.from(existingPopups).forEach((popup) => popup.remove());
            
                    new mapboxgl.Popup({ offset: 25, maxWidth: '600px', className: 'popup' })
                    .setLngLat(location)
                    .setHTML(`
                        <div>
                        <div class='alert-popup-title'>${res.data.name}</div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Description:</div>
                            <div class='alert-popup-row-value'>${res.data.description ? formatPolicyAndUniqueIDs(res.data.description) : 'No Description'}</div>
                        </div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Type:</div>
                            <div class='alert-popup-row-value'>${res.data.type === 'point' ? 'POINT/ADDRESS' : 'POLYGON'}</div>
                        </div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Radius:</div>
                            <div class='alert-popup-row-value'>
                            ${res.data.radius_miles} miles
                            </div>
                        </div>
                        ${res.data.value ?
                            `<div class='alert-popup-row'>
                                <div class='alert-popup-row-name'>Total Asset Value:</div>
                                <div class='alert-popup-row-value'>
                                $${Number(res.data.value).toLocaleString()}
                                </div>
                            </div>` : ' '}
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Created</div>
                            <div class='alert-popup-row-value'>${convertTo12HourFormat(res.data.created_timestamp)}</div>
                        </div>
                        </div>
                    `)
                    .addTo(map.current);
  
                    const generateCircleCoordinates = (center, radius, points = 64) => {
                        const [centerLng, centerLat] = center;
                        const coordinates = [];
                      
                        for (let i = 0; i < points; i++) {
                            const angle = (i * 360) / points;
                            const angleRad = (angle * Math.PI) / 180;
                      
                            const latitude = centerLat + (radius / 69) * Math.cos(angleRad);
                            const longitude =
                                centerLng +
                                (radius / (69 * Math.cos((centerLat * Math.PI) / 180))) *
                                    Math.sin(angleRad);
                      
                            coordinates.push([longitude, latitude]);
                        }
                        coordinates.push(coordinates[0]);
                        return coordinates;
                      };
                  
                    const circleCoordinates = generateCircleCoordinates([res.data.longitude, res.data.latitude], res.data.radius_miles);
        
                    const geometry = {
                        type: 'Feature',
                        properties: res.data,
                        geometry: {
                            type: 'Polygon',
                            coordinates: [circleCoordinates],
                        }
                    }
                    
                    map.current.addSource(`Source-areas-focused-${res.data.id}`, {
                        type: 'geojson',
                        data: {
                            type: 'FeatureCollection',
                            features: [geometry],
                        },
                    });
            
                    map.current.addLayer({
                        'id': `Layer-areas-focused-${res.data.id}-fill`,
                        'type': 'fill',
                        'source': `Source-areas-focused-${res.data.id}`,
                        'layout': {},
                        'paint': {
                          'fill-color': 'rgba(240, 240, 240, 0.4)',
                        }
                    });
          
                    map.current.addLayer({
                        'id': `Layer-areas-focused-${res.data.id}-outline`,
                        'type': 'line',
                        'source': `Source-areas-focused-${res.data.id}`,
                        'layout': {},
                        'paint': {
                          'line-color': 'rgba(255, 255, 255, 1)',
                          'line-width': 2
                        }
                    });
        
                    map.current.fitBounds(turf.bbox(geometry), {
                        padding: 100,
                        pitch: 0
                    });

                    setPanel({
                        name: 'Empty', 
                        content: null
                    });
                }).catch((err) => {
                    console.error('Error adding areas:', err);
                    setLoading(false);
                    setError('Error creating Monitoring Area. Please try again.');
                });
                }).catch((error) => {
                    console.log('error getting token', error);
                    setLoading(false);
                });
            })
        }
    };


    return (
        <div className='create-area-content-instructions'>
            <div className='create-area-content-instructions-instruction'>
                <div
                    className='create-area-content-instructions-instruction-title'
                    style={{ marginBottom: '0.4rem' }}
                >
                    Step 1: Enter Address
                </div>
                <SearchBox
                    accessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN} 
                    value=''
                    onRetrieve={(e) => {
                        const location = e.features[0].geometry.coordinates;
 
                        setCircle({
                            name: e.features[0].properties ? returnAreaName(e.features[0].properties) : 'Unknown',
                            description: `Proximity: ${radius} miles (${(radius * 1.60934).toFixed(2)} Kilometers)`,
                            coordinates: location
                        });

                        const point = document.createElement('div');
                        point.className = 'map-pin-marker';
                        const marker = new mapboxgl.Marker(point)
                            .setLngLat(location)
                            .addTo(map.current);
               
                        locationRef.current = marker;
                    }}
                />
            </div>
            <div className='create-area-content-instructions-instruction'>
                <div
                    className='create-area-content-instructions-instruction-title'
                    style={{ marginBottom: '0.4rem' }}
                >
                    Step 2: Define Proximity
                </div>
                <div className='create-area-content-slider-container'>
                    <input
                        disabled={!(circle.coordinates.length > 0)}
                        style={!circle.coordinates.length > 0 ? { cursor: 'not-allowed' } : {}}
                        className='create-area-content-slider-container-slider'
                        type='range' 
                        min='1' 
                        max='200' 
                        step='10'
                        value={radius} 
                        onChange={(e) => setRadius(Number(e.target.value))}
                    />
                  <div className='create-area-content-slider-container-input-container' >
                    <input
                        type='text'
                        disabled={!(circle.coordinates.length > 0)}
                        style={!circle.coordinates.length > 0 ? { cursor: 'not-allowed' } : {}}
                        value={radius}
                        onChange={(e) => {
                            const value = e.target.value;

                            if (/^\d*$/.test(value)) { // Ensure only numbers
                                const numericValue = Number(value);
                                if (numericValue >= 1 && numericValue <= 200) {
                                    setRadius(numericValue);
                                } else if (value === '') {
                                    setRadius(''); // Allow clearing the input temporarily
                                }
                            }
                        }}
                        onBlur={(e) => {
                            if (!e.target.value) {
                                setRadius(1);
                            }
                        }}
                        className='create-area-content-slider-container-input-container-input' 
                    />
                    <div className='create-area-content-slider-container-input-container-text'>mile(s)</div>
                  </div>
                  <div className='create-area-content-form-subtitle' style={{ marginTop: '1rem' }}>Do you want to add this area for yourself or for your team(s)?</div>
                    <div className='create-area-content-form-radios'>
                        <div className='create-area-content-form-radios-radio'>
                            <input
                            type="radio"
                            name="teamOption"
                            value="false"
                            checked={!addToTeam}
                            onChange={(e) => setAddToTeam(e.target.value === 'true')}
                            />
                            Only for me
                        </div>
                        <div className='create-area-content-form-radios-radio'>
                            <input
                                type="radio"
                                name="teamOption"
                                value="true"
                                checked={addToTeam}
                                onChange={(e) => setAddToTeam(e.target.value === 'true')}
                            />
                            Add to team
                        </div>
                    </div>
                    {(addToTeam && teams) && 
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        {teams && teams.map(team => (
                            <div
                                key={team.id}
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: '10px',
                                    marginBottom: '10px',
                                    background: 'rgba(203, 203, 203, 0.58)',
                                    padding: '0.7rem',
                                    borderRadius: 10
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedTeams.some(selected => selected.id === team.id)}
                                    // The event returns the checkbox's checked state (true/false)
                                    onChange={(e) => handleTeamCheck(team, e.target.checked)}
                                    aria-label="teams-checkbox"
                                />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                    <span style={{ fontSize: '14px', color: 'black', fontWeight: 'bold' }}>
                                    {team.name}
                                    </span>
                                    <span style={{ fontSize: '13px', color: 'black' }}>
                                    {team.description}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                    }
                </div>
                {loading ?
                <Spinner />
                :
                <div 
                    className={(circle.coordinates.length > 0 && (radius >= 1 && radius <= 200)) ? 'create-area-content-instructions-instruction-next-btn' : 'create-area-content-instructions-instruction-disabled-btn'}
                    onClick={savePolygon}
                    style={{ marginTop: '1rem' }}
                    >Create Area
                </div>}
                {error && 
                <div 
                    className='create-area-content-form-error'
                    style={{ marginTop: '1rem' }}
                    >{error}</div>}
            </div>
        </div>
    );
}
